import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE_URL, API_DOMAIN } from "../utils/config";
import { useToast } from "../context/ToastContext";
import { 
  FiPlus, FiEdit, FiTrash2, FiAward, 
  FiBriefcase, FiX,
  FiFileText, FiUpload, FiInfo
} from "react-icons/fi";
import "./EmployeeDashboard.css";

const CandidateEducation = () => {
  const { showToast } = useToast();

  const [educationList, setEducationList] = useState([]);
  const [isEduModalOpen, setIsEduModalOpen] = useState(false);
  const [eduFormData, setEduFormData] = useState({
    qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: ""
  });
  const [editingEduId, setEditingEduId] = useState(null);

  const [experienceList, setExperienceList] = useState([]);
  const [isExpModalOpen, setIsExpModalOpen] = useState(false);
  const [expFormData, setExpFormData] = useState({
    companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', 
    offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null
  });
  const [editingExpId, setEditingExpId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("candidateToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getCandidateId = () => localStorage.getItem("candidateId") || null;

  const renderDocumentUrl = (path) => {
    if (!path) return "#";
    if (path.startsWith("http")) return path;
    const normalizedPath = path.replace(/\\/g, '/');
    if (normalizedPath.includes('/uploads/')) {
      const parts = normalizedPath.split('/uploads/');
      return `${API_DOMAIN}/uploads/${parts[1]}`;
    }
    const cleanPath = normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath;
    return `${API_DOMAIN}/${cleanPath}`;
  };

  const fetchData = async () => {
    setLoading(true);
    const candidateId = getCandidateId();
    const headers = getAuthHeaders();

    // Education - alag try catch
    try {
      const eduRes = await axios.get(`${API_BASE_URL}/candidate/education`, { headers, params: { candidateId } });
      setEducationList(eduRes.data?.data || eduRes.data || []);
    } catch (err) {
      console.error("Education fetch failed:", err.message);
      setEducationList([]);
    }

    // Experience - alag try catch, education fail hone se affect nahi hoga
    try {
      const expRes = await axios.get(`${API_BASE_URL}/candidate/experience`, { headers, params: { candidateId } });
      setExperienceList(expRes.data?.data || expRes.data || []);
    } catch (err) {
      console.error("Experience fetch failed:", err.message);
      setExperienceList([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEduSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...eduFormData, candidateId: getCandidateId() };
      let res;
      if (editingEduId) {
        res = await axios.put(`${API_BASE_URL}/candidate/education/${editingEduId}`, payload, { headers: getAuthHeaders() });
      } else {
        res = await axios.post(`${API_BASE_URL}/candidate/education`, payload, { headers: getAuthHeaders() });
      }
      if (res.data.success) {
        showToast("Academic record synchronized", "success");
        fetchData();
        setIsEduModalOpen(false);
        setEditingEduId(null);
        setEduFormData({ qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: "" });
      }
    } catch (err) {
      showToast("Transmission failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEduEdit = (edu) => {
    setEditingEduId(edu._id);
    setEduFormData({
      qualification: edu.qualification || "",
      institution: edu.institution || "",
      startDate: edu.startDate ? new Date(edu.startDate).toISOString().split('T')[0] : "",
      endDate: edu.endDate ? new Date(edu.endDate).toISOString().split('T')[0] : "",
      passingYear: edu.passingYear || "",
      location: edu.location || "",
      percentage: edu.percentage || ""
    });
    setIsEduModalOpen(true);
  };

  const handleEduDelete = async (id) => {
    if (!window.confirm("Purge this academic record?")) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/candidate/education/${id}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        showToast("Record purged", "success");
        fetchData();
      }
    } catch (err) {
      showToast("Action failed", "error");
    }
  };

  const handleExpSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    Object.keys(expFormData).forEach(key => {
      if (expFormData[key]) data.append(key, expFormData[key]);
    });
    data.append("candidateId", getCandidateId());

    try {
      let res;
      const headers = { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() };
      if (editingExpId) {
        res = await axios.put(`${API_BASE_URL}/candidate/experience/${editingExpId}`, data, { headers });
      } else {
        res = await axios.post(`${API_BASE_URL}/candidate/experience`, data, { headers });
      }
      if (res.data?.success || res.status === 200 || res.status === 201) {
        showToast("Professional record synchronized", "success");
        fetchData();
        setIsExpModalOpen(false);
        setEditingExpId(null);
        setExpFormData({ companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null });
      }
    } catch (err) {
      showToast("Transmission failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExpEdit = (exp) => {
    setEditingExpId(exp._id);
    setExpFormData({
      companyName: exp.companyName || '',
      role: exp.role || '',
      location: exp.location || '',
      salary: exp.salary || '',
      startDate: exp.startDate ? new Date(exp.startDate).toISOString().split('T')[0] : '',
      endDate: exp.endDate ? new Date(exp.endDate).toISOString().split('T')[0] : '',
      offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null
    });
    setIsExpModalOpen(true);
  };

  const handleExpDelete = async (id) => {
    if (!window.confirm("Purge this professional record?")) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/candidate/experience/${id}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        showToast("Record purged", "success");
        fetchData();
      }
    } catch (err) {
      showToast("Action failed", "error");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'PRESENT';

  return (
    <div className="emp-dash">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Academic & <span>Professional</span>
            </h1>
            <p className="emp-dash__subtitle">
              Education and career history records
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiAward />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Educational Qualifications */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600 flex items-center justify-center shadow-inner border border-emerald-200/50">
                <FiAward size={24} />
              </div>
              <div>
                <h3 className="emp-dash__card-title">Educational Qualifications</h3>
                <p className="emp-dash__card-desc">Academic history & credentials</p>
              </div>
            </div>
            <button onClick={() => setIsEduModalOpen(true)} className="h-11 px-6 bg-indigo-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2">
              <FiPlus size={12} /> Add Qualification
            </button>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            <div className="overflow-x-auto">
              <table className="min-w-[1000px]">
                <thead className="text-[11px] text-gray-700 uppercase tracking-widest font-bold border-b border-gray-200">
                  <tr>
                    <th className="py-4 px-6 text-left font-bold">Qualification</th>
                    <th className="py-4 px-6 text-left font-bold">Institution</th>
                    <th className="py-4 px-6 text-center font-bold">Period / Year</th>
                    <th className="py-4 px-6 text-center font-bold">Location</th>
                    <th className="py-4 px-6 text-center font-bold">Score (%)</th>
                    <th className="py-4 px-6 text-center font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">Loading Records...</p>
                      </td>
                    </tr>
                  ) : educationList.length > 0 ? (
                    educationList.map((edu) => (
                      <tr key={edu._id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">{edu.qualification}</span>
                            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Degree</span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">{edu.institution}</span>
                            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Institution</span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <div className="text-xs font-bold text-gray-800">{edu.passingYear}</div>
                          {edu.startDate && <div className="text-[8px] font-bold text-gray-400 mt-0.5">{new Date(edu.startDate).getFullYear()} - {new Date(edu.endDate).getFullYear()}</div>}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className="text-[10px] font-bold text-gray-600 uppercase">{edu.location}</span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-widest border border-emerald-100 shadow-sm">
                            {edu.percentage}%
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <div className="flex justify-center gap-3">
                            <button onClick={() => handleEduEdit(edu)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm">
                              <FiEdit size={16} />
                            </button>
                            <button onClick={() => handleEduDelete(edu._id)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white border border-rose-100 transition-all transform active:scale-90 shadow-sm">
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6}>
                        <div className="p-16 text-center">
                          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-emerald-200 border border-emerald-100/50 shadow-sm">
                            <FiAward size={30} />
                          </div>
                          <h3 className="text-base font-bold text-gray-700 mb-1">No Educational Records</h3>
                          <p className="text-[11px] text-gray-400 font-bold tracking-widest uppercase max-w-xs mx-auto leading-relaxed">Use the button above to add your first educational qualification.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Professional Experience */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 flex items-center justify-center shadow-inner border border-blue-200/50">
                <FiBriefcase size={24} />
              </div>
              <div>
                <h3 className="emp-dash__card-title">Professional Experience</h3>
                <p className="emp-dash__card-desc">Career history & documents</p>
              </div>
            </div>
            <button onClick={() => setIsExpModalOpen(true)} className="h-11 px-6 bg-indigo-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2">
              <FiPlus size={12} /> Add Experience
            </button>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            <div className="overflow-x-auto">
              <table className="min-w-[1000px]">
                <thead className="text-[11px] text-gray-700 uppercase tracking-widest font-bold border-b border-gray-200">
                  <tr>
                    <th className="py-4 px-6 text-left font-bold">Company & Role</th>
                    <th className="py-4 px-6 text-left font-bold">Location</th>
                    <th className="py-4 px-6 text-center font-bold">Tenure Period</th>
                    <th className="py-4 px-6 text-center font-bold">Salary</th>
                    <th className="py-4 px-6 text-center font-bold">Documents</th>
                    <th className="py-4 px-6 text-center font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">Loading Records...</p>
                      </td>
                    </tr>
                  ) : experienceList.length > 0 ? (
                    experienceList.map((exp) => (
                      <tr key={exp._id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">{exp.role}</span>
                            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">{exp.companyName}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">{exp.location}</span>
                            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Location</span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <div className="text-xs font-bold text-gray-800">{formatDate(exp.startDate)} — {formatDate(exp.endDate)}</div>
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 block">Tenure</span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-widest border border-blue-100 shadow-sm">
                            ₹{exp.salary}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <div className="flex justify-center gap-3">
                            {exp.offerLetter && <a href={renderDocumentUrl(exp.offerLetter)} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm" title="Offer Letter"><FiFileText size={16} /></a>}
                            {exp.payslip && <a href={renderDocumentUrl(exp.payslip)} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white border border-emerald-100 transition-all transform active:scale-90 shadow-sm" title="Payslip"><FiFileText size={16} /></a>}
                            {exp.relievingLetter && <a href={renderDocumentUrl(exp.relievingLetter)} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-orange-50 text-orange-700 hover:bg-orange-600 hover:text-white border border-orange-100 transition-all transform active:scale-90 shadow-sm" title="Relieving Letter"><FiFileText size={16} /></a>}
                            {exp.otherDocument && <a href={renderDocumentUrl(exp.otherDocument)} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-purple-50 text-purple-700 hover:bg-purple-600 hover:text-white border border-purple-100 transition-all transform active:scale-90 shadow-sm" title="Other Document"><FiFileText size={16} /></a>}
                            {!exp.offerLetter && !exp.payslip && !exp.relievingLetter && !exp.otherDocument && <span className="text-[9px] font-bold text-gray-300 uppercase">No Docs</span>}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <div className="flex justify-center gap-3">
                            <button onClick={() => handleExpEdit(exp)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm">
                              <FiEdit size={16} />
                            </button>
                            <button onClick={() => handleExpDelete(exp._id)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white border border-rose-100 transition-all transform active:scale-90 shadow-sm">
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6}>
                        <div className="p-16 text-center">
                          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-200 border border-blue-100/50 shadow-sm">
                            <FiBriefcase size={30} />
                          </div>
                          <h3 className="text-base font-bold text-gray-700 mb-1">No Professional Records</h3>
                          <p className="text-[11px] text-gray-400 font-bold tracking-widest uppercase max-w-xs mx-auto leading-relaxed">Use the button above to add your first professional experience.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest py-4">
          <FiInfo className="text-blue-400" />
          Timely Health Academic & Professional Records • {new Date().getFullYear()}
        </div>
      </div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {/* Education Modal */}
        {isEduModalOpen && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-xl rounded-3xl md:rounded-[3rem] shadow-2xl p-6 md:p-12 border border-gray-100 relative max-h-[95vh] overflow-y-auto">
              <button className="absolute top-4 md:top-8 right-4 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100"
                onClick={() => { setIsEduModalOpen(false); setEditingEduId(null); setEduFormData({ qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: "" }); }}>
                <FiX size={16} />
              </button>
              <div className="mb-8 md:mb-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-emerald-50 text-emerald-500 rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-5 shadow-inner border border-emerald-100"><FiAward size={30} /></div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 uppercase tracking-[0.1em]">{editingEduId ? "Update Academy" : "Log Academy"}</h3>
                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-2">Credentials Sync Protocol</p>
              </div>
              <form onSubmit={handleEduSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Qualification</label>
                  <input type="text" required value={eduFormData.qualification} onChange={e => setEduFormData({...eduFormData, qualification: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="e.g. B.Tech" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Institution</label>
                  <input type="text" required value={eduFormData.institution} onChange={e => setEduFormData({...eduFormData, institution: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="University Name" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                  <input type="date" value={eduFormData.startDate} onChange={e => setEduFormData({...eduFormData, startDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">End Date</label>
                  <input type="date" value={eduFormData.endDate} onChange={e => setEduFormData({...eduFormData, endDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Passing Year</label>
                  <input type="text" required value={eduFormData.passingYear} onChange={e => setEduFormData({...eduFormData, passingYear: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="2024" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Location</label>
                  <input type="text" value={eduFormData.location} onChange={e => setEduFormData({...eduFormData, location: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="City, State" />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Score (%)</label>
                  <input type="text" required value={eduFormData.percentage} onChange={e => setEduFormData({...eduFormData, percentage: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="85.5" />
                </div>
                <div className="md:col-span-2 pt-6">
                  <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-2xl text-[11px] font-bold uppercase tracking-[0.3em] shadow-xl shadow-emerald-100 hover:scale-[1.01] transition-all active:scale-95 disabled:opacity-50">
                    {isSubmitting ? "Transmitting..." : editingEduId ? "Update Records" : "Save Records"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Experience Modal */}
        {isExpModalOpen && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-2xl rounded-3xl md:rounded-[3rem] shadow-2xl p-6 md:p-12 border border-gray-100 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
              <button className="absolute top-4 md:top-8 right-4 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100"
                onClick={() => { setIsExpModalOpen(false); setEditingExpId(null); setExpFormData({ companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null }); }}>
                <FiX size={16} />
              </button>
              <div className="mb-8 md:mb-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 text-blue-500 rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-5 shadow-inner border border-blue-100"><FiBriefcase size={30} /></div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 uppercase tracking-[0.1em]">{editingExpId ? "Update Career" : "Log Career"}</h3>
                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-2">Professional Sync Protocol</p>
              </div>
              <form onSubmit={handleExpSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Company Name</label>
                  <input type="text" required value={expFormData.companyName} onChange={e => setExpFormData({...expFormData, companyName: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" placeholder="Acme Corp" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Role</label>
                  <input type="text" required value={expFormData.role} onChange={e => setExpFormData({...expFormData, role: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" placeholder="Sr. Engineer" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Location</label>
                  <input type="text" required value={expFormData.location} onChange={e => setExpFormData({...expFormData, location: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" placeholder="City, Country" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Salary</label>
                  <input type="text" required value={expFormData.salary} onChange={e => setExpFormData({...expFormData, salary: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" placeholder="e.g. 50,000" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                  <input type="date" required value={expFormData.startDate} onChange={e => setExpFormData({...expFormData, startDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">End Date (Current)</label>
                  <input type="date" value={expFormData.endDate} onChange={e => setExpFormData({...expFormData, endDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-[10px] font-bold outline-none" />
                </div>

                <div className="md:col-span-2 grid grid-cols-2 gap-4 mt-4">
                  <div className="p-5 bg-indigo-50/50 border-2 border-dashed border-indigo-200 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-indigo-100 transition relative">
                    <FiUpload className="text-indigo-500" />
                    <span className="text-[9px] font-bold text-indigo-700 uppercase tracking-widest text-center">{expFormData.offerLetter ? expFormData.offerLetter.name : "OFFER LETTER"}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, offerLetter: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                  <div className="p-5 bg-emerald-50/50 border-2 border-dashed border-emerald-200 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-emerald-100 transition relative">
                    <FiUpload className="text-emerald-500" />
                    <span className="text-[9px] font-bold text-emerald-700 uppercase tracking-widest text-center">{expFormData.payslip ? expFormData.payslip.name : "PAYSLIP"}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, payslip: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                  <div className="p-5 bg-orange-50/50 border-2 border-dashed border-orange-200 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-orange-100 transition relative">
                    <FiUpload className="text-orange-500" />
                    <span className="text-[9px] font-bold text-orange-700 uppercase tracking-widest text-center">{expFormData.relievingLetter ? expFormData.relievingLetter.name : "RELIEVING LETTER"}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, relievingLetter: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                  <div className="p-5 bg-purple-50/50 border-2 border-dashed border-purple-200 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-purple-100 transition relative">
                    <FiUpload className="text-purple-500" />
                    <span className="text-[9px] font-bold text-purple-700 uppercase tracking-widest text-center">{expFormData.otherDocument ? expFormData.otherDocument.name : "OTHER DOCUMENT"}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, otherDocument: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                </div>

                <div className="md:col-span-2 pt-6">
                  <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl text-[11px] font-bold uppercase tracking-[0.3em] shadow-xl shadow-blue-100 hover:scale-[1.01] transition-all active:scale-95 disabled:opacity-50">
                    {isSubmitting ? "Transmitting..." : editingExpId ? "Update Career" : "Authorize Log"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-center gap-3 text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-10">
        <FiInfo className="text-indigo-400" />
        Synchronized Professional & Academic Records Protocol • {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default CandidateEducation;